import React, { useState, useEffect } from 'react';
import {
  Plus,
  Building,
  Wallet,
  TrendingUp,
  Edit,
  Trash2,
  Eye,
  DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const CashFlowBanks = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [statement, setStatement] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    bank_name: '',
    account_type: 'checking',
    account_number: '',
    agency: '',
    initial_balance: '',
    notes: ''
  });

  const [adjustData, setAdjustData] = useState({
    new_balance: '',
    reason: ''
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cash-flow/banks');
      setAccounts(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      toast.error('Erro ao carregar contas bancárias');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatement = async (accountId) => {
    try {
      const response = await api.get(`/api/cash-flow/banks/${accountId}/statement`);
      setStatement(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar extrato:', error);
      toast.error('Erro ao carregar extrato');
      setStatement([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAccount) {
        await api.put(`/api/cash-flow/banks/${editingAccount.id}`, formData);
        toast.success('Conta atualizada com sucesso!');
      } else {
        await api.post('/api/cash-flow/banks', formData);
        toast.success('Conta criada com sucesso!');
      }

      setShowModal(false);
      setEditingAccount(null);
      resetForm();
      loadAccounts();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar conta');
    }
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/api/cash-flow/banks/${selectedAccount.id}/adjust-balance`, adjustData);
      toast.success('Saldo ajustado com sucesso!');
      setShowAdjustModal(false);
      setSelectedAccount(null);
      setAdjustData({ new_balance: '', reason: '' });
      loadAccounts();
    } catch (error) {
      console.error('Erro ao ajustar saldo:', error);
      toast.error(error.response?.data?.message || 'Erro ao ajustar saldo');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Excluir esta conta bancária?')) return;

    try {
      await api.delete(`/api/cash-flow/banks/${id}`);
      toast.success('Conta excluída!');
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao excluir conta');
    }
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      bank_name: account.bank_name || '',
      account_type: account.account_type,
      account_number: account.account_number || '',
      agency: account.agency || '',
      initial_balance: account.initial_balance,
      notes: account.notes || ''
    });
    setShowModal(true);
  };

  const openStatementModal = async (account) => {
    setSelectedAccount(account);
    setShowStatementModal(true);
    await loadStatement(account.id);
  };

  const openAdjustModal = (account) => {
    setSelectedAccount(account);
    setAdjustData({
      new_balance: account.current_balance,
      reason: ''
    });
    setShowAdjustModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      bank_name: '',
      account_type: 'checking',
      account_number: '',
      agency: '',
      initial_balance: '',
      notes: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getAccountTypeLabel = (type) => {
    const types = {
      cash: 'Caixa',
      checking: 'Conta Corrente',
      savings: 'Poupança',
      investment: 'Investimento'
    };
    return types[type] || type;
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + parseFloat(acc.current_balance || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contas Bancárias</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie suas contas e saldos
          </p>
        </div>

        <button
          onClick={() => {
            setEditingAccount(null);
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nova Conta
        </button>
      </div>

      {/* Resumo Total */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80 mb-2">Saldo Total</p>
            <p className="text-4xl font-bold">{formatCurrency(totalBalance)}</p>
          </div>
          <Wallet className="w-16 h-16 opacity-30" />
        </div>
        <p className="text-sm opacity-80 mt-4">
          {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'} ativa{accounts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Lista de Contas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((account) => (
          <div
            key={account.id}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {account.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {getAccountTypeLabel(account.account_type)}
                  </p>
                </div>
              </div>
            </div>

            {account.bank_name && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {account.bank_name}
              </p>
            )}

            {account.agency && account.account_number && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Ag: {account.agency} | CC: {account.account_number}
              </p>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Saldo Atual</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(account.current_balance)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Saldo Inicial</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatCurrency(account.initial_balance)}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => openStatementModal(account)}
                className="flex-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center justify-center gap-2 text-sm"
              >
                <Eye className="w-4 h-4" />
                Extrato
              </button>
              <button
                onClick={() => openAdjustModal(account)}
                className="flex-1 px-3 py-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 flex items-center justify-center gap-2 text-sm"
              >
                <DollarSign className="w-4 h-4" />
                Ajustar
              </button>
            </div>

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => openEditModal(account)}
                className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-2 text-sm"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              <button
                onClick={() => handleDelete(account.id)}
                className="flex-1 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center gap-2 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          </div>
        ))}
      </div>

      {accounts.length === 0 && !loading && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl">
          <Building className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Nenhuma conta cadastrada</p>
        </div>
      )}

      {/* Modal de Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {editingAccount ? 'Editar Conta' : 'Nova Conta Bancária'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome da Conta *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Banco
                    </label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo *
                    </label>
                    <select
                      value={formData.account_type}
                      onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      required
                    >
                      <option value="cash">Caixa</option>
                      <option value="checking">Conta Corrente</option>
                      <option value="savings">Poupança</option>
                      <option value="investment">Investimento</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Agência
                    </label>
                    <input
                      type="text"
                      value={formData.agency}
                      onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Número da Conta
                    </label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </div>
                </div>

                {!editingAccount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Saldo Inicial
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.initial_balance}
                      onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    {editingAccount ? 'Atualizar' : 'Criar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAccount(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Extrato */}
      {showStatementModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Extrato - {selectedAccount.name}
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                        Data
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                        Descrição
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                        Categoria
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {statement.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {new Date(item.transaction_date).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {item.category_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          <span className={item.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                            {item.type === 'income' ? '+' : '-'} {formatCurrency(item.amount)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {statement.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">Nenhuma transação encontrada</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => {
                    setShowStatementModal(false);
                    setSelectedAccount(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ajuste de Saldo */}
      {showAdjustModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Ajustar Saldo - {selectedAccount.name}
              </h2>

              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Saldo Atual</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(selectedAccount.current_balance)}
                </p>
              </div>

              <form onSubmit={handleAdjustBalance} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Novo Saldo *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjustData.new_balance}
                    onChange={(e) => setAdjustData({ ...adjustData, new_balance: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Motivo do Ajuste *
                  </label>
                  <textarea
                    value={adjustData.reason}
                    onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    Ajustar Saldo
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAdjustModal(false);
                      setSelectedAccount(null);
                      setAdjustData({ new_balance: '', reason: '' });
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlowBanks;